import { Empty, Tooltip } from 'antd';
import { useEffect, useRef } from 'react';
import type { InventoryDeviceView } from '../inventory-integration/inventoryMapper';
import type { InventoryDeviceReadState } from '../inventory-integration/inventoryPageRuntime';
import styles from './MarketingAnnouncement.module.css';
import type { MarketingAnnouncement } from './marketingAnnouncementApi';

export type AnnouncementDeviceLabel = { name?: string; sku?: string | null };

export function splitAnnouncementCategories(
  categories: MarketingAnnouncement['categories'],
) {
  if (categories.length < 2) return [categories];
  const total = categories.reduce(
    (sum, category) => sum + Math.max(1, category.deviceIds.length),
    0,
  );
  let bestIndex = 1;
  let leftCount = 0;
  let bestDifference = Number.POSITIVE_INFINITY;
  for (let index = 0; index < categories.length - 1; index += 1) {
    leftCount += Math.max(1, categories[index].deviceIds.length);
    const difference = Math.abs(total - leftCount * 2);
    if (difference < bestDifference) {
      bestDifference = difference;
      bestIndex = index + 1;
    }
  }
  return [categories.slice(0, bestIndex), categories.slice(bestIndex)];
}

function Quantity({
  inventory,
  readState,
  canRead,
}: {
  inventory?: InventoryDeviceView;
  readState?: InventoryDeviceReadState;
  canRead: boolean;
}) {
  if (!canRead)
    return (
      <Tooltip title="当前账号没有库存查看权限">
        <span>—</span>
      </Tooltip>
    );
  if (readState?.status === 'loading' || (!readState && !inventory))
    return <span title="库存读取中">…</span>;
  if (readState?.status === 'error' || readState?.status === 'empty')
    return (
      <Tooltip
        title={
          readState.status === 'error'
            ? '库存读取失败，请稍后重试'
            : '当前设备不在可读库存范围内'
        }
      >
        <span>—</span>
      </Tooltip>
    );
  const count = inventory?.totalDisplayQuantity;
  if (inventory?.readStatus !== 'KNOWN' || typeof count !== 'number') {
    return (
      <Tooltip title="六仓库存尚未全部读取成功">
        <span>—</span>
      </Tooltip>
    );
  }
  return (
    <Tooltip
      title={
        inventory.observedAt
          ? `六仓库存合计，读取于 ${new Date(inventory.observedAt).toLocaleString('zh-CN')}`
          : '六仓库存合计'
      }
    >
      <strong className={count < 0 ? styles.negativeQuantity : styles.quantity}>
        {count}
      </strong>
    </Tooltip>
  );
}

export function MarketingAnnouncementContent({
  announcement,
  inventoryById,
  inventoryReadStateById,
  deviceLabels,
  canReadInventory,
  onVisibleDevices,
  compact = false,
  snapshot = false,
}: {
  announcement: MarketingAnnouncement;
  inventoryById: ReadonlyMap<string, InventoryDeviceView>;
  inventoryReadStateById?: ReadonlyMap<string, InventoryDeviceReadState>;
  deviceLabels?: ReadonlyMap<string, AnnouncementDeviceLabel>;
  canReadInventory: boolean;
  onVisibleDevices?: (deviceIds: string[]) => void;
  compact?: boolean;
  snapshot?: boolean;
}) {
  const tablesRef = useRef<HTMLDivElement>(null);
  const categories = announcement.categories.filter(
    (category) => category.enabled,
  );
  const columns = splitAnnouncementCategories(categories);
  const [first, second] = announcement.headlineLines;
  useEffect(() => {
    if (snapshot || !onVisibleDevices || !tablesRef.current) return;
    const rows =
      tablesRef.current.querySelectorAll<HTMLElement>('tr[data-device-id]');
    if (typeof IntersectionObserver === 'undefined') {
      onVisibleDevices([
        ...new Set(
          [...rows]
            .map((row) => row.dataset.deviceId)
            .filter((id): id is string => Boolean(id)),
        ),
      ]);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const ids = [
          ...new Set(
            entries
              .filter((entry) => entry.isIntersecting)
              .map((entry) => (entry.target as HTMLElement).dataset.deviceId)
              .filter((id): id is string => Boolean(id)),
          ),
        ];
        if (ids.length) onVisibleDevices(ids);
      },
      { root: tablesRef.current, rootMargin: '48px 0px' },
    );
    rows.forEach((row) => {
      observer.observe(row);
    });
    return () => observer.disconnect();
  }, [announcement.categories, onVisibleDevices, snapshot]);
  return (
    <div
      className={`${styles.announcementContent} ${compact ? styles.compact : ''}`}
    >
      {first.text.trim() || second.text.trim() ? (
        <div className={styles.announcementIntro}>
          {first.text.trim() ? (
            <h1 style={{ color: first.color }}>{first.text}</h1>
          ) : null}
          {second.text.trim() ? (
            <h2 style={{ color: second.color }}>{second.text}</h2>
          ) : null}
        </div>
      ) : null}
      {!snapshot ? (
        <div className={styles.inventoryMeta}>六仓总库存</div>
      ) : null}
      {categories.length === 0 ? (
        <Empty
          className={styles.empty}
          description="暂无启用的类型"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <div
          ref={tablesRef}
          className={`${styles.announcementTables} ${columns.length === 2 ? styles.announcementTablesDouble : ''}`}
        >
          {columns.map((column, columnIndex) => (
            <table
              className={styles.inventoryTable}
              key={columnIndex === 0 ? 'left' : 'right'}
            >
              <colgroup>
                <col className={styles.typeColumn} />
                <col />
                {!snapshot ? <col className={styles.quantityColumn} /> : null}
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">类型</th>
                  <th scope="col">货品编号</th>
                  {!snapshot ? <th scope="col">数量</th> : null}
                </tr>
              </thead>
              <tbody>
                {column.map((category) => {
                  const ids = category.deviceIds;
                  if (!ids.length)
                    return (
                      <tr key={category.name}>
                        <th scope="rowgroup" className={styles.typeCell}>
                          {category.name || '未命名类型'}
                        </th>
                        <td className={styles.emptyDevice}>暂无可展示设备</td>
                        {!snapshot ? (
                          <td className={styles.quantityCell}>—</td>
                        ) : null}
                      </tr>
                    );
                  return ids.map((deviceId, index) => {
                    const inventory = snapshot
                      ? undefined
                      : inventoryById.get(deviceId);
                    const label = deviceLabels?.get(deviceId);
                    const name =
                      inventory?.deviceName || label?.name || '货品名称不可见';
                    const sku = inventory?.deviceSku || label?.sku;
                    return (
                      <tr
                        key={`${category.name}-${deviceId}`}
                        data-device-id={deviceId}
                      >
                        {index === 0 ? (
                          <th
                            scope="rowgroup"
                            rowSpan={ids.length}
                            className={styles.typeCell}
                          >
                            {category.name || '未命名类型'}
                          </th>
                        ) : null}
                        <td className={styles.deviceName}>
                          <Tooltip
                            title={sku ? `${name}（${sku}）` : name}
                            trigger={['hover', 'focus', 'click']}
                          >
                            <button
                              type="button"
                              className={styles.deviceNameText}
                              title={sku ? `${name}（${sku}）` : name}
                            >
                              {name}
                            </button>
                          </Tooltip>
                        </td>
                        {!snapshot ? (
                          <td className={styles.quantityCell}>
                            <Quantity
                              inventory={inventory}
                              readState={inventoryReadStateById?.get(deviceId)}
                              canRead={canReadInventory}
                            />
                          </td>
                        ) : null}
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          ))}
        </div>
      )}
    </div>
  );
}
